// Partes de la pantalla de Carga para el control de la carga (decisión D54): el estado del control del depósito, el
// "Recibí conforme" con la firma, el motivo de lo que sube en parte y los renglones de cada pedido con − y +.
import { formatoHora, MOTIVOS_FALTANTE, type QuienTilda } from '@reparto/shared/nucleo';
import { Boton, CampoNumero, Chip, cx, HojaInferior, Icono, Tarjeta } from '@reparto/ui';
import { useState } from 'react';
import type { Despacho, ItemPedido, PedidoCarga } from '../datos/tipos';
import { Firma, type FirmaHecha } from './Firma';

/** Lo que pidió el cliente de un artículo (si sube en parte, más de lo que sube). */
export const pedidoDe = (i: Pick<ItemPedido, 'bultos' | 'bultos_pedidos'>) => i.bultos_pedidos ?? i.bultos;
export const enParte = (p: Pick<PedidoCarga, 'items'>) => p.items.some((i) => i.bultos < pedidoDe(i));

/** "Controlado por Ana" si lo tildó alguien del depósito; "Lo tildaste vos" si fue el chofer. */
export function marcaDe(p: PedidoCarga, yo: string | undefined): { texto: string; deposito: boolean } | null {
  if (!p.cargado) return null;
  const quien = p.controlado_por;
  if (quien && quien.rol !== 'chofer') return { texto: `Controlado por ${quien.nombre}`, deposito: true };
  if (!quien && p.cargado_por_encargado_en && !p.cargado_por_chofer_en)
    return { texto: 'Controlado por el depósito', deposito: true };
  if (quien && quien.id !== yo) return { texto: `Tildado por ${quien.nombre}`, deposito: false };
  return { texto: 'Lo tildaste vos', deposito: false };
}

/** Estado del control: qué hizo el depósito, qué hizo el chofer y qué falta para salir. */
export function TarjetaControl({
  despacho,
  quien,
  yo,
  pendienteLocal,
}: {
  despacho: Despacho | undefined;
  quien: QuienTilda;
  yo: string | undefined;
  pendienteLocal: { en: string } | null | undefined;
}) {
  const c = despacho?.controlado ?? null;
  const r = despacho?.recibido ?? null;
  const miRecibido = r && r.por?.id === yo;
  const renglon = (icono: string, tono: 'ok' | 'aviso' | 'espera', texto: string) => (
    <li className="flex gap-2">
      <Icono
        nombre={icono}
        tamano={20}
        className={cx(
          'mt-0.5 shrink-0',
          tono === 'ok' ? 'text-primary-600' : tono === 'aviso' ? 'text-amber-600' : 'text-stone-400',
        )}
      />
      <span>{texto}</span>
    </li>
  );
  return (
    <Tarjeta className="space-y-2" aria-label="Control de la carga">
      <h2 className="text-sm font-medium">
        {quien === 'ambos' ? 'Controlan el depósito y vos' : 'Controla el depósito'}
      </h2>
      <ul className="space-y-1.5 text-sm">
        {c
          ? c.vigente
            ? renglon(
                'verified',
                'ok',
                `${c.por?.nombre ?? 'El encargado'} controló la carga a las ${formatoHora(c.en!)}.`,
              )
            : renglon(
                'history',
                'aviso',
                `${c.por?.nombre ?? 'El encargado'} controló a las ${formatoHora(c.en!)}, pero la carga cambió después: tiene que volver a controlar.`,
              )
          : renglon('hourglass_empty', 'espera', 'El encargado todavía no dio "Controlé la carga".')}
        {quien === 'ambos' || r || pendienteLocal
          ? pendienteLocal
            ? renglon(
                'cloud_upload',
                'aviso',
                `Tu "Recibí conforme" de las ${formatoHora(pendienteLocal.en)} se envía cuando haya señal. Para salir a ruta tiene que llegar.`,
              )
            : r
              ? r.vigente
                ? renglon(
                    'draw',
                    'ok',
                    `${miRecibido ? 'Diste' : `${r.por?.nombre ?? 'El chofer'} dio`} el "Recibí conforme" a las ${formatoHora(r.en!)}.`,
                  )
                : renglon('history', 'aviso', 'La carga cambió después del "Recibí conforme": hay que darlo de nuevo.')
              : renglon('draw', 'espera', 'Falta tu "Recibí conforme" con la firma.')
          : null}
      </ul>
    </Tarjeta>
  );
}

/** Un artículo del pedido: cuánto sube (con − y + si se puede cambiar) y, si sube en parte, cuánto pidió y por qué. */
export function RenglonItem({
  item,
  editable,
  onCambiar,
}: {
  item: ItemPedido;
  editable: boolean;
  onCambiar: (bultos: number) => void;
}) {
  const pidio = pedidoDe(item);
  const parte = item.bultos < pidio;
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="min-w-0">
        <span className="block truncate">
          <span className="text-stone-500 tabular-nums">{item.codigo}</span> {item.nombre}
        </span>
        {parte ? (
          <span className="text-xs text-amber-700 dark:text-amber-300">
            {item.bultos === 0 ? 'No sube' : `Sube ${item.bultos} de ${pidio}`}
            {item.motivo_carga ? ` · ${item.motivo_carga}` : ''}
          </span>
        ) : null}
      </span>
      {editable ? (
        <CampoNumero
          valor={item.bultos}
          min={0}
          max={pidio}
          etiqueta={`Bultos de ${item.nombre} que suben`}
          onCambiar={onCambiar}
        />
      ) : (
        <span className="shrink-0 tabular-nums">
          {item.bultos} {item.unidad}
        </span>
      )}
    </div>
  );
}

/** Por qué sube menos de lo pedido: los motivos rápidos y "Otro" para escribirlo. */
export function HojaFaltante({
  abierta,
  item,
  bultos,
  onCerrar,
  onConfirmar,
}: {
  abierta: boolean;
  item: ItemPedido | null;
  bultos: number;
  onCerrar: () => void;
  onConfirmar: (motivo: string) => void;
}) {
  const [motivo, setMotivo] = useState<string | null>(null);
  const [otro, setOtro] = useState('');
  const texto = motivo === 'Otro' ? otro.trim() : motivo;
  return (
    <HojaInferior abierta={abierta} onCerrar={onCerrar} titulo="¿Por qué sube menos?">
      {item ? (
        <div className="space-y-4">
          <p className="text-sm text-stone-600 dark:text-stone-300">
            {item.nombre}: {bultos === 0 ? 'no sube ninguno' : `suben ${bultos} de ${pedidoDe(item)}`}. El pedido sale
            en parte y el cliente se entera.
          </p>
          <div className="flex flex-wrap gap-2">
            {MOTIVOS_FALTANTE.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMotivo(m)}
                className={cx(
                  'h-10 rounded-full border px-4 text-sm',
                  motivo === m
                    ? 'border-primary-600 bg-primary-600 text-white'
                    : 'border-stone-300 dark:border-stone-600',
                )}
              >
                {m}
              </button>
            ))}
          </div>
          {motivo === 'Otro' ? (
            <input
              className="h-11 w-full rounded-xl border border-stone-300 bg-white px-3 dark:border-stone-600 dark:bg-stone-900"
              placeholder="Contá el motivo"
              aria-label="Motivo"
              value={otro}
              onChange={(e) => setOtro(e.target.value)}
            />
          ) : null}
          <Boton
            bloque
            tamano="lg"
            disabled={!texto}
            onClick={() => {
              if (!texto) return;
              onConfirmar(texto);
              setMotivo(null);
              setOtro('');
            }}
          >
            Sube en parte
          </Boton>
        </div>
      ) : null}
    </HojaInferior>
  );
}

/**
 * "Recibí conforme" (decisión D54): el resumen de lo que sube (completo, en parte y lo que no sube, con su motivo), la
 * firma en el recuadro y una observación. Sin señal se guarda y se envía al volver.
 */
interface PropsConforme {
  pedidos: PedidoCarga[];
  motivos: Record<string, string>;
  onMotivo: (pedidoId: string, motivo: string) => void;
  motivosNoSube: readonly string[];
  enviando: boolean;
  onConfirmar: (firma: FirmaHecha, observacion: string) => void;
}

export function HojaConforme({
  abierta,
  onCerrar,
  ...resto
}: PropsConforme & { abierta: boolean; onCerrar: () => void }) {
  // El contenido (con el lienzo de la firma) se arma al abrir: cada vez empieza en blanco y con su tamaño real.
  return (
    <HojaInferior abierta={abierta} onCerrar={onCerrar} titulo="Recibí conforme">
      {abierta ? <ContenidoConforme {...resto} /> : null}
    </HojaInferior>
  );
}

function ContenidoConforme({ pedidos, motivos, onMotivo, motivosNoSube, enviando, onConfirmar }: PropsConforme) {
  const [firma, setFirma] = useState<FirmaHecha | null>(null);
  const [observacion, setObservacion] = useState('');
  const suben = pedidos.filter((p) => p.cargado);
  const noSuben = pedidos.filter((p) => !p.cargado);
  const parte = suben.filter(enParte);
  const bultos = suben.reduce((s, p) => s + p.bultos_total, 0);
  const faltaMotivo = noSuben.some((p) => !(motivos[p.id] ?? p.no_cargado_motivo));
  return (
    <>
      <div className="max-h-[70vh] space-y-4 overflow-y-auto">
        <p className="text-sm text-stone-700 dark:text-stone-200">
          Subís{' '}
          <strong>
            {suben.length} {suben.length === 1 ? 'pedido' : 'pedidos'}
          </strong>{' '}
          con{' '}
          <strong>
            {bultos} {bultos === 1 ? 'bulto' : 'bultos'}
          </strong>
          {parte.length ? `; ${parte.length} ${parte.length === 1 ? 'va' : 'van'} en parte` : ''}.
        </p>
        {parte.length ? (
          <ul className="space-y-1 text-sm">
            {parte.map((p) => (
              <li key={p.id} className="rounded-xl bg-amber-50 px-3 py-2 dark:bg-amber-900/30">
                <span className="font-medium">
                  {p.numero_visible} · {p.cliente.nombre}
                </span>
                <span className="block text-xs text-amber-800 dark:text-amber-200">
                  {p.items
                    .filter((i) => i.bultos < pedidoDe(i))
                    .map(
                      (i) => `− ${pedidoDe(i) - i.bultos} ${i.nombre}${i.motivo_carga ? ` (${i.motivo_carga})` : ''}`,
                    )
                    .join(' · ')}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
        {noSuben.length ? (
          <div className="space-y-3">
            <p className="text-sm font-medium">No suben {noSuben.length}:</p>
            {noSuben.map((p) => {
              const actual = motivos[p.id] ?? p.no_cargado_motivo ?? '';
              return (
                <div key={p.id}>
                  <div className="mb-1.5 text-sm">
                    {p.numero_visible} · {p.cliente.nombre}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {motivosNoSube.map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => onMotivo(p.id, m)}
                        className={cx(
                          'h-9 rounded-full border px-3 text-sm',
                          actual === m
                            ? 'border-primary-600 bg-primary-600 text-white'
                            : 'border-stone-300 dark:border-stone-600',
                        )}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
        <div>
          <p className="mb-1.5 text-sm font-medium">Tu firma</p>
          <Firma onCambio={setFirma} etiqueta="Firma del chofer" />
        </div>
        <input
          className="h-11 w-full rounded-xl border border-stone-300 bg-white px-3 text-sm dark:border-stone-600 dark:bg-stone-900"
          placeholder="Observación (opcional)"
          aria-label="Observación"
          value={observacion}
          onChange={(e) => setObservacion(e.target.value)}
        />
      </div>
      <div className="pt-4">
        {faltaMotivo ? (
          <p className="mb-2 text-center text-xs text-rose-700 dark:text-rose-300">
            Elegí por qué no sube cada pedido.
          </p>
        ) : null}
        <Boton
          bloque
          tamano="lg"
          icono="draw"
          cargando={enviando}
          disabled={!firma || faltaMotivo || !suben.length}
          onClick={() => firma && onConfirmar(firma, observacion)}
        >
          Recibí conforme
        </Boton>
      </div>
    </>
  );
}

/** Chips del pedido en la lista: quién lo controló y si sube en parte. */
export function ChipsControl({ p, yo }: { p: PedidoCarga; yo: string | undefined }) {
  const marca = marcaDe(p, yo);
  return (
    <>
      {marca ? (
        <Chip tono={marca.deposito ? 'exito' : 'neutro'} icono={marca.deposito ? 'verified' : 'person'}>
          {marca.texto}
        </Chip>
      ) : null}
      {enParte(p) ? (
        <Chip tono="aviso" icono="rule">
          En parte
        </Chip>
      ) : null}
    </>
  );
}
